import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import type { Role } from '../types';

interface RoleContextValue {
  role: Role;
  loading: boolean;
  isAdmin: boolean;
  isCde: boolean;
  isGovernance: boolean;
  /** KPI figures are CdE/governance only; status is visible to everyone. */
  canSeeKpis: boolean;
  /** Who may drive a process through the phases. */
  canEditProcess: boolean;
}

const RoleContext = createContext<RoleContextValue>({
  role: 'user', loading: true, isAdmin: false, isCde: false,
  isGovernance: false, canSeeKpis: false, canEditProcess: false,
});

export function RoleProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  // The role is "loaded" only for the account it was fetched for. Deriving
  // `loading` from that (instead of a flag set inside the effect) makes it true
  // the very render the account appears — the router, a child of this
  // provider, runs its effect before ours and otherwise saw role=user for one
  // tick and bounced an admin reloading #/admin to the répertoire.
  const [state, setState] = useState<{ forUser: number | null; role: Role }>({ forUser: null, role: 'user' });

  useEffect(() => {
    if (!user) { setState({ forUser: null, role: 'user' }); return; }
    // The kit's /auth/me carries the account; the CdE role lives on our own
    // users table and is served by /api/roles/me.
    let cancelled = false;
    fetch('/api/roles/me', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (!cancelled) setState({ forUser: user.id, role: (d?.role as Role) || 'user' }); })
      .catch(() => { if (!cancelled) setState({ forUser: user.id, role: 'user' }); });
    return () => { cancelled = true; };
  }, [user?.id]);

  const role = state.role;
  const loading = !!user && state.forUser !== user.id;

  const isAdmin = role === 'admin';
  const isCde = role === 'cde' || isAdmin;
  const isGovernance = role === 'governance' || isAdmin;

  return (
    <RoleContext.Provider value={{
      role, loading, isAdmin, isCde, isGovernance,
      canSeeKpis: isAdmin || isCde || isGovernance,
      canEditProcess: isAdmin || isCde,
    }}>
      {children}
    </RoleContext.Provider>
  );
}

export const useRole = () => useContext(RoleContext);
